import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calculator, Save, Send, Trash2, ArrowLeft, Package, Wrench, Clock, FileText, Mail, MessageCircle, Printer } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import SearchableSelect from '../components/ui/SearchableSelect';
import Textarea from '../components/ui/Textarea';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';
import produtosService from '../services/produtos';
import servicosService from '../services/servicos';
import ordensServicoService from '../services/ordensServico';
import orcamentosService from '../services/orcamentos';
import notificationsService from '../services/notifications';
// Usar o tipo OrdemServico já definido em types
const OrcamentoFormPage = () => {
    const navigate = useNavigate();
    const { orcamentoId } = useParams();
    const { success, error } = useToast();
    const isEditing = !!orcamentoId;
    // Estados
    const [formData, setFormData] = useState({
        ordemServicoId: 0,
        prazoReparacao: 3,
        prazoGarantia: 3,
        observacoes: '',
        observacoesInternas: '',
        validadeDias: 7,
        itens: []
    });
    const [selectedOS, setSelectedOS] = useState(null);
    const [currentOrcamentoId, setCurrentOrcamentoId] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [sendOptions, setSendOptions] = useState({
        email: false,
        whatsapp: false,
        print: false
    });
    const [pdfUrl, setPdfUrl] = useState('');
    const [isGeneratingCupom, setIsGeneratingCupom] = useState(false);
    // Estados para pesquisa de peças e serviços
    const [pecasOptions, setPecasOptions] = useState([]);
    const [servicosOptions, setServicosOptions] = useState([]);
    const [isLoadingPecas, setIsLoadingPecas] = useState(false);
    const [isLoadingServicos, setIsLoadingServicos] = useState(false);
    // Buscar ordens de serviço do backend (apenas OSs ativas, não canceladas/concluídas)
    const { data: ordensServicoResponse, isLoading: isLoadingOS } = useQuery({
        queryKey: ['ordens-servico-para-orcamento'],
        queryFn: () => ordensServicoService.list({ limit: 100 }), // Buscar todas as OS disponíveis
        staleTime: 1000 * 60 * 5, // Cache por 5 minutos
    });
    // Query para carregar orçamento existente (quando editando)
    const { data: orcamentoExistente, isLoading: isLoadingOrcamento } = useQuery({
        queryKey: ['orcamento', orcamentoId],
        queryFn: () => orcamentosService.getById(parseInt(orcamentoId)),
        enabled: isEditing && !!orcamentoId,
        staleTime: 0 // Sempre buscar dados atualizados
    });
    // Filtrar apenas OSs que podem receber orçamento (não canceladas, não concluídas, não entregues, não aguardando aprovação)
    const ordensDisponiveis = ordensServicoResponse?.ordensServico?.filter(os => {
        const estadoNome = os.estado?.nome || '';
        const isEstadoFinal = [
            'CANCELADO', 'CONCLUÍDO', 'ENTREGUE', 'CANCELADA', 'CONCLUÍDA', 'ENTREGUE',
            'AGUARDA APROVAÇÃO', 'AGUARDANDO APROVAÇÃO'
        ].includes(estadoNome.toUpperCase());
        return !isEstadoFinal;
    }) || [];
    // Mock data para peças e serviços
    const mockPecas = [
        { id: 1, nome: 'Tela LCD iPhone 13 Pro', valor: 150.00 },
        { id: 2, nome: 'Bateria iPhone 13 Pro', valor: 89.90 },
        { id: 3, nome: 'Bateria Samsung Galaxy S21', valor: 65.00 },
        { id: 4, nome: 'Tela Samsung Galaxy S21', valor: 125.00 }
    ];
    const mockServicos = [
        { id: 1, nome: 'Troca de tela', valor: 35.50 },
        { id: 2, nome: 'Substituição de bateria', valor: 24.90 },
        { id: 3, nome: 'Limpeza e manutenção', valor: 15.00 },
        { id: 4, nome: 'Diagnóstico completo', valor: 20.00 }
    ];
    useEffect(() => {
        if (formData.ordemServicoId && ordensDisponiveis.length > 0) {
            const os = ordensDisponiveis.find(os => os.id === formData.ordemServicoId);
            setSelectedOS(os || null);
        }
    }, [formData.ordemServicoId, ordensDisponiveis]);
    // Carregar dados do orçamento existente quando editando
    useEffect(() => {
        if (orcamentoExistente && isEditing) {
            console.log('Carregando orçamento existente:', orcamentoExistente);
            setCurrentOrcamentoId(orcamentoExistente.id);
            setFormData({
                ordemServicoId: orcamentoExistente.ordemServicoId,
                prazoReparacao: parseInt(orcamentoExistente.prazoReparacao?.replace(' dias', '') || '3'),
                prazoGarantia: parseInt(orcamentoExistente.garantia?.replace(' meses', '') || '3'),
                observacoes: orcamentoExistente.observacoesCliente || '',
                observacoesInternas: orcamentoExistente.observacoesInternas || '',
                validadeDias: orcamentoExistente.validadeAte ?
                    Math.ceil((new Date(orcamentoExistente.validadeAte).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 7,
                itens: orcamentoExistente.itens?.map(item => ({
                    id: item.id,
                    tipo: item.tipo,
                    descricao: item.descricao,
                    quantidade: item.quantidade,
                    valorUnitario: item.valorUnitario,
                    valorTotal: item.quantidade * item.valorUnitario
                })) || []
            });
        }
    }, [orcamentoExistente, isEditing]);
    const calcularValorTotal = () => {
        return formData.itens.reduce((total, item) => total + item.valorTotal, 0);
    };
    const adicionarItem = (tipo) => {
        const novoItem = {
            tipo,
            descricao: '',
            quantidade: 1,
            valorUnitario: 0,
            valorTotal: 0
        };
        setFormData({
            ...formData,
            itens: [...formData.itens, novoItem]
        });
    };
    const atualizarItem = (index, field, value) => {
        const novosItens = [...formData.itens];
        novosItens[index] = { ...novosItens[index], [field]: value };
        // Recalcular valor total do item
        if (field === 'quantidade' || field === 'valorUnitario') {
            novosItens[index].valorTotal = novosItens[index].quantidade * novosItens[index].valorUnitario;
        }
        setFormData({ ...formData, itens: novosItens });
    };
    const removerItem = (index) => {
        const novosItens = formData.itens.filter((_, i) => i !== index);
        setFormData({ ...formData, itens: novosItens });
    };
    const selecionarPecaOuServico = (index, tipo, itemId) => {
        const lista = tipo === 'PECA' ? mockPecas : mockServicos;
        const item = lista.find(p => p.id.toString() === itemId);
        if (item) {
            atualizarItem(index, 'descricao', item.nome);
            atualizarItem(index, 'valorUnitario', item.valor);
        }
    };
    const handleSave = async (status = 'RASCUNHO') => {
        if (!formData.ordemServicoId) {
            error('Selecione uma ordem de serviço');
            return;
        }
        if (formData.itens.length === 0) {
            error('Adicione pelo menos um item ao orçamento');
            return;
        }
        try {
            // Criar data de validade
            const hoje = new Date();
            const validadeAte = new Date(hoje.getTime() + (formData.validadeDias * 24 * 60 * 60 * 1000));
            // Preparar dados para o backend
            const orcamentoData = {
                ordemServicoId: formData.ordemServicoId,
                prazoReparacao: `${formData.prazoReparacao} dias`,
                garantia: `${formData.prazoGarantia} meses`,
                validadeAte: validadeAte.toISOString(),
                observacoesCliente: formData.observacoes,
                observacoesInternas: formData.observacoesInternas,
                itens: formData.itens.map(item => ({
                    tipo: item.tipo,
                    descricao: item.descricao,
                    quantidade: item.quantidade,
                    valorUnitario: item.valorUnitario
                }))
            };
            let orcamentoId = currentOrcamentoId;
            let isNewOrcamento = false;
            // Se não existe orçamento, criar novo
            if (!orcamentoId) {
                const orcamento = await orcamentosService.create(orcamentoData);
                orcamentoId = orcamento.id;
                setCurrentOrcamentoId(orcamentoId);
                isNewOrcamento = true;
            }
            else {
                // Se existe orçamento, atualizar os dados
                await orcamentosService.update(orcamentoId, orcamentoData);
            }
            if (status === 'ENVIADO') {
                // Atualizar status para enviado (isso acionará a mudança de estado da OS)
                await orcamentosService.send(orcamentoId);
                success('Orçamento atualizado e enviado com sucesso! O estado da ordem de serviço foi atualizado.');
            }
            else {
                success(isEditing ? 'Orçamento atualizado com sucesso!' : 'Orçamento salvo como rascunho!');
                // Se é um orçamento novo (não é edição), mostrar modal de envio
                if (isNewOrcamento) {
                    await generateCupomPreview();
                    setShowSendModal(true);
                    return; // Não navegar ainda, esperar o usuário confirmar ou fechar o modal
                }
            }
            // Salvar PDF automaticamente em background
            try {
                // Buscar orçamento completo para gerar token
                const orcamentoCompleto = await orcamentosService.findById(orcamentoId);
                if (orcamentoCompleto) {
                    const token = generatePublicToken(orcamentoCompleto.id, orcamentoCompleto.criadoEm);
                    // Gerar PDF em background - não aguardar para não atrasar o usuário
                    fetch(`/api/public/orcamento/${orcamentoCompleto.id}/${token}/pdf`, {
                        method: 'GET'
                    }).catch(err => console.warn('PDF em background falhou:', err));
                }
            }
            catch (err) {
                console.warn('Erro ao salvar PDF automaticamente:', err);
                // Não mostrar erro para o usuário, é apenas um backup
            }
            navigate('/orcamentos');
        }
        catch (err) {
            console.error('Erro ao salvar orçamento:', err);
            error(`Erro ao salvar orçamento: ${err.response?.data?.message || err.message}`);
        }
    };
    const handleSend = async () => {
        await generateCupomPreview();
        setShowSendModal(true);
    };
    const generateCupomPreview = async () => {
        setIsGeneratingCupom(true);
        try {
            // Primeiro salvar o orçamento se ainda não foi salvo
            if (!formData.ordemServicoId) {
                error('Selecione uma ordem de serviço');
                return;
            }
            if (formData.itens.length === 0) {
                error('Adicione pelo menos um item ao orçamento');
                return;
            }
            let orcamentoId = currentOrcamentoId;
            // Se ainda não existe orçamento, criar um novo
            if (!orcamentoId) {
                // Criar data de validade
                const hoje = new Date();
                const validadeAte = new Date(hoje.getTime() + (formData.validadeDias * 24 * 60 * 60 * 1000));
                // Preparar dados para o backend
                const orcamentoData = {
                    ordemServicoId: formData.ordemServicoId,
                    prazoReparacao: `${formData.prazoReparacao} dias`,
                    garantia: `${formData.prazoGarantia} meses`,
                    validadeAte: validadeAte.toISOString(),
                    observacoesCliente: formData.observacoes,
                    observacoesInternas: formData.observacoesInternas,
                    itens: formData.itens.map(item => ({
                        tipo: item.tipo,
                        descricao: item.descricao,
                        quantidade: item.quantidade,
                        valorUnitario: item.valorUnitario
                    }))
                };
                // Criar orçamento como rascunho
                const orcamento = await orcamentosService.create(orcamentoData);
                orcamentoId = orcamento.id;
                setCurrentOrcamentoId(orcamentoId);
            }
            // Gerar URL do PDF com token de autenticação
            const token = localStorage.getItem('token');
            const url = `/api/orcamentos/${orcamentoId}/cupom/orcamento?token=${token}`;
            setPdfUrl(url);
        }
        catch (err) {
            console.error('Erro ao gerar preview:', err);
            error(`Erro ao gerar orçamento: ${err.response?.data?.message || err.message}`);
        }
        finally {
            setIsGeneratingCupom(false);
        }
    };
    const generateAndPrintCupom = async () => {
        if (!currentOrcamentoId || !pdfUrl)
            return;
        try {
            // Abrir PDF em nova janela para impressão
            const printWindow = window.open(pdfUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
                success('Cupom PDF aberto para impressão');
            }
            else {
                error('Bloqueador de pop-up impediu a abertura do PDF');
            }
        }
        catch (err) {
            console.error('Erro ao imprimir cupom:', err);
            error('Erro ao imprimir cupom PDF');
        }
    };
    // Gerar token público para o orçamento
    const generatePublicToken = (orcamentoId, criadoEm) => {
        return btoa(`${orcamentoId}-${criadoEm}`).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
    };
    // Gerar URL pública para o orçamento
    const generatePublicURL = (orcamentoId, token) => {
        const baseURL = window.location.origin.replace(':4051', ':4050'); // Trocar porta do frontend pela do backend
        return `${baseURL}/api/public/orcamento/${orcamentoId}/${token}`;
    };
    const confirmarEnvio = async () => {
        try {
            console.log('🚀 confirmarEnvio - Iniciando com opções:', sendOptions);
            // Se pelo menos uma das opções foi selecionada, mudar estado para ENVIADO/Aguarda Aprovação
            if (sendOptions.email || sendOptions.whatsapp || sendOptions.print) {
                setShowSendModal(false);
                console.log('📝 Orçamento ID:', currentOrcamentoId);
                // Primeiro atualizar status para ENVIADO se necessário
                if (currentOrcamentoId) {
                    console.log('📤 Atualizando status para ENVIADO...');
                    await orcamentosService.send(currentOrcamentoId);
                    console.log('✅ Status atualizado');
                }
                // Gerar link público para o orçamento
                if (currentOrcamentoId && (sendOptions.email || sendOptions.whatsapp)) {
                    console.log('🔗 Gerando link público...');
                    // Buscar o orçamento recém-criado/atualizado para obter a data de criação
                    const orcamento = await orcamentosService.getById(currentOrcamentoId);
                    console.log('✅ Orçamento obtido:', orcamento);
                    const token = generatePublicToken(currentOrcamentoId, orcamento.criadoEm);
                    const publicURL = generatePublicURL(currentOrcamentoId, token);
                    console.log('🔗 URL pública gerada:', publicURL);
                    if (sendOptions.whatsapp) {
                        console.log('📱 Enviando WhatsApp...');
                        await sendWhatsAppMessage(orcamento, publicURL);
                    }
                    if (sendOptions.email) {
                        console.log('📧 Enviando Email...');
                        await sendEmailMessage(orcamento, publicURL);
                    }
                }
                if (sendOptions.print) {
                    console.log('🖨️ Imprimindo cupom...');
                    // Imprimir cupom PDF
                    if (pdfUrl) {
                        await generateAndPrintCupom();
                    }
                }
                // Navegar de volta para a lista de orçamentos
                console.log('✅ Processo concluído, navegando para /orcamentos');
                navigate('/orcamentos');
            }
            else {
                error('Selecione pelo menos uma opção: Email, WhatsApp ou Imprimir');
            }
        }
        catch (err) {
            console.error('❌ Erro em confirmarEnvio:', err);
            error(`Erro ao processar orçamento: ${err.message}`);
        }
    };
    const fecharModalSemEnviar = () => {
        setShowSendModal(false);
        navigate('/orcamentos');
    };
    // Enviar mensagem via WhatsApp
    const sendWhatsAppMessage = async (orcamento, publicURL) => {
        try {
            console.log('📱 sendWhatsAppMessage - Iniciando:', { orcamentoId: orcamento.id, publicURL });
            // Verificar se cliente tem telefone cadastrado
            if (!selectedOS?.cliente.telefonePrincipal) {
                console.error('❌ Cliente não possui telefone cadastrado');
                error('Cliente não possui telefone cadastrado');
                return;
            }
            console.log('📤 Enviando notificação via API...');
            // Enviar notificação usando o sistema integrado
            const result = await notificationsService.sendOrcamentoNotification(orcamento.id, 'CRIACAO');
            console.log('✅ Resposta da API:', result);
            success('Notificação enviada por WhatsApp com sucesso');
        }
        catch (err) {
            console.error('❌ Erro ao enviar WhatsApp:', err);
            console.error('❌ Erro completo:', JSON.stringify(err, null, 2));
            error(err.response?.data?.message || err.message || 'Erro ao enviar por WhatsApp');
        }
    };
    // Enviar email
    const sendEmailMessage = async (orcamento, publicURL) => {
        try {
            // Verificar se cliente tem email cadastrado
            if (!selectedOS?.cliente.email) {
                error('Cliente não possui email cadastrado');
                return;
            }
            // Enviar notificação usando o sistema integrado
            await notificationsService.sendOrcamentoNotification(orcamento.id, 'CRIACAO');
            success('Notificação enviada por email com sucesso');
        }
        catch (err) {
            console.error('Erro ao enviar email:', err);
            error(err.response?.data?.message || 'Erro ao enviar por email');
        }
    };
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-PT', {
            style: 'currency',
            currency: 'EUR'
        }).format(value);
    };
    // Funções de pesquisa para peças e serviços
    const searchPecas = useCallback(async (search) => {
        setIsLoadingPecas(true);
        try {
            const pecas = await produtosService.searchPecas(search);
            const options = pecas.map(peca => ({
                value: peca.id.toString(),
                label: `${peca.nome} - ${formatCurrency(peca.precoVenda)}`,
                data: peca
            }));
            setPecasOptions(options);
        }
        catch (error) {
            console.error('Erro ao buscar peças:', error);
            setPecasOptions([]);
        }
        finally {
            setIsLoadingPecas(false);
        }
    }, []);
    const searchServicos = useCallback(async (search) => {
        setIsLoadingServicos(true);
        try {
            const servicos = await servicosService.searchServicos(search);
            const options = servicos.map(servico => ({
                value: servico.id.toString(),
                label: `${servico.nome} - ${formatCurrency(servico.preco)}`,
                data: servico
            }));
            setServicosOptions(options);
        }
        catch (error) {
            console.error('Erro ao buscar serviços:', error);
            setServicosOptions([]);
        }
        finally {
            setIsLoadingServicos(false);
        }
    }, []);
    const selecionarPecaOuServicoNovo = (index, value, option) => {
        if (!option?.data)
            return;
        const novosItens = [...formData.itens];
        const item = option.data;
        novosItens[index] = {
            ...novosItens[index],
            descricao: item.nome,
            valorUnitario: item.precoVenda || item.preco,
            valorTotal: novosItens[index].quantidade * (item.precoVenda || item.preco)
        };
        setFormData({ ...formData, itens: novosItens });
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "bg-white shadow-sm border-b px-6 py-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsx(Button, { variant: "ghost", onClick: () => navigate('/orcamentos'), leftIcon: _jsx(ArrowLeft, { className: "h-4 w-4" }), children: "Voltar" }), _jsx(Calculator, { className: "h-8 w-8 text-blue-600" }), _jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900", children: isEditing ? 'Editar Orçamento' : 'Novo Orçamento' }), _jsx("p", { className: "text-gray-600", children: selectedOS ? `${selectedOS.numero} - ${selectedOS.cliente.nome}` : 'Selecione uma ordem de serviço' })] })] }), _jsxs("div", { className: "flex gap-3", children: [_jsx(Button, { variant: "outline", onClick: () => handleSave('RASCUNHO'), leftIcon: _jsx(Save, { className: "h-4 w-4" }), children: "Salvar Rascunho" }), _jsx(Button, { onClick: handleSend, leftIcon: _jsx(Send, { className: "h-4 w-4" }), disabled: !selectedOS || formData.itens.length === 0, children: "Enviar para Cliente" })] })] }) }), _jsxs("div", { className: "max-w-6xl mx-auto px-6 space-y-6", children: [_jsxs("div", { className: "bg-white rounded-lg shadow-sm border p-6", children: [_jsx("h2", { className: "text-lg font-medium mb-4", children: "Informa\u00E7\u00F5es B\u00E1sicas" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsx(Select, { label: "Ordem de Servi\u00E7o *", value: formData.ordemServicoId.toString(), onChange: (e) => setFormData({ ...formData, ordemServicoId: parseInt(e.target.value) }), disabled: isLoadingOS, options: [
                                            { value: '0', label: isLoadingOS ? 'Carregando...' : 'Selecione uma OS' },
                                            ...ordensDisponiveis.map(os => ({
                                                value: os.id.toString(),
                                                label: `${os.numero} - ${os.cliente.nome} (${os.equipamento.modelo.marca.nome} ${os.equipamento.modelo.nome})`
                                            }))
                                        ] }), _jsx(Input, { label: "Validade do Or\u00E7amento (dias)", type: "number", value: formData.validadeDias, onChange: (e) => setFormData({ ...formData, validadeDias: parseInt(e.target.value) || 7 }), min: 1, max: 30 }), _jsx(Input, { label: "Prazo de Repara\u00E7\u00E3o (dias)", type: "number", value: formData.prazoReparacao, onChange: (e) => setFormData({ ...formData, prazoReparacao: parseInt(e.target.value) || 1 }), min: 1, max: 30 }), _jsx(Input, { label: "Prazo de Garantia (meses)", type: "number", value: formData.prazoGarantia, onChange: (e) => setFormData({ ...formData, prazoGarantia: parseInt(e.target.value) || 1 }), min: 1, max: 24 })] }), selectedOS && (_jsxs("div", { className: "mt-4 p-4 bg-gray-50 rounded-lg", children: [_jsx("h3", { className: "font-medium text-gray-900 mb-2", children: "Detalhes da OS" }), _jsxs("div", { className: "grid grid-cols-2 gap-4 text-sm", children: [_jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Cliente:" }), _jsx("span", { className: "ml-2 font-medium", children: selectedOS.cliente.nome })] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Equipamento:" }), _jsxs("span", { className: "ml-2 font-medium", children: [selectedOS.equipamento.modelo.marca.nome, " ", selectedOS.equipamento.modelo.nome] })] }), _jsxs("div", { className: "col-span-2", children: [_jsx("span", { className: "text-gray-600", children: "Problema:" }), _jsx("span", { className: "ml-2 font-medium", children: selectedOS.problemaRelatado })] })] })] }))] }), _jsxs("div", { className: "bg-white rounded-lg shadow-sm border p-6", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsx("h2", { className: "text-lg font-medium", children: "Itens do Or\u00E7amento" }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", size: "sm", onClick: () => adicionarItem('PECA'), leftIcon: _jsx(Package, { className: "h-4 w-4" }), children: "Adicionar Pe\u00E7a" }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => adicionarItem('SERVICO'), leftIcon: _jsx(Wrench, { className: "h-4 w-4" }), children: "Adicionar Servi\u00E7o" })] })] }), _jsxs("div", { className: "space-y-4", children: [formData.itens.map((item, index) => (_jsxs("div", { className: "border rounded-lg p-4", children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [item.tipo === 'PECA' ? (_jsx(Package, { className: "h-5 w-5 text-blue-500" })) : (_jsx(Wrench, { className: "h-5 w-5 text-green-500" })), _jsxs("span", { className: "font-medium", children: [item.tipo === 'PECA' ? 'Peça' : 'Serviço', " #", index + 1] })] }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => removerItem(index), className: "text-red-600 hover:text-red-800", children: _jsx(Trash2, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-5 gap-4", children: [_jsxs("div", { className: "md:col-span-2", children: [_jsx(SearchableSelect, { label: `Pesquisar ${item.tipo === 'PECA' ? 'Peça' : 'Serviço'}`, placeholder: `Digite para pesquisar ${item.tipo === 'PECA' ? 'peças' : 'serviços'}...`, value: "", options: item.tipo === 'PECA' ? pecasOptions : servicosOptions, onChange: (value, option) => selecionarPecaOuServicoNovo(index, value, option), onSearch: item.tipo === 'PECA' ? searchPecas : searchServicos, isLoading: item.tipo === 'PECA' ? isLoadingPecas : isLoadingServicos }), _jsx(Input, { label: "Descri\u00E7\u00E3o *", value: item.descricao, onChange: (e) => atualizarItem(index, 'descricao', e.target.value), placeholder: "Descri\u00E7\u00E3o do item", className: "mt-2" })] }), _jsx(Input, { label: "Quantidade *", type: "number", value: item.quantidade, onChange: (e) => atualizarItem(index, 'quantidade', parseInt(e.target.value) || 0), min: 1 }), _jsx(Input, { label: "Valor Unit\u00E1rio *", type: "number", step: "0.01", value: item.valorUnitario, onChange: (e) => atualizarItem(index, 'valorUnitario', parseFloat(e.target.value) || 0), min: 0 }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Valor Total" }), _jsx("div", { className: "mt-1 text-lg font-semibold text-gray-900", children: formatCurrency(item.valorTotal) })] })] })] }, index))), formData.itens.length === 0 && (_jsxs("div", { className: "text-center py-8 text-gray-500", children: [_jsx(Calculator, { className: "h-12 w-12 mx-auto mb-4 opacity-50" }), _jsx("p", { children: "Nenhum item adicionado ao or\u00E7amento" }), _jsx("p", { className: "text-sm", children: "Clique em \"Adicionar Pe\u00E7a\" ou \"Adicionar Servi\u00E7o\" para come\u00E7ar" })] }))] }), formData.itens.length > 0 && (_jsx("div", { className: "mt-6 pt-6 border-t", children: _jsx("div", { className: "flex justify-end", children: _jsxs("div", { className: "text-right", children: [_jsx("p", { className: "text-sm text-gray-600", children: "Valor Total do Or\u00E7amento" }), _jsx("p", { className: "text-2xl font-bold text-gray-900", children: formatCurrency(calcularValorTotal()) })] }) }) }))] }), _jsxs("div", { className: "bg-white rounded-lg shadow-sm border p-6", children: [_jsx("h2", { className: "text-lg font-medium mb-4", children: "Observa\u00E7\u00F5es" }), _jsxs("div", { className: "space-y-4", children: [_jsx(Textarea, { label: "Observa\u00E7\u00F5es para o Cliente", value: formData.observacoes, onChange: (e) => setFormData({ ...formData, observacoes: e.target.value }), placeholder: "Informa\u00E7\u00F5es importantes que o cliente deve saber...", rows: 4 }), _jsx(Textarea, { label: "Observa\u00E7\u00F5es Internas", value: formData.observacoesInternas || '', onChange: (e) => setFormData({ ...formData, observacoesInternas: e.target.value }), placeholder: "Notas internas para a equipe...", rows: 3 })] })] })] }), _jsx(Modal, { isOpen: showSendModal, onClose: () => {
                    setShowSendModal(false);
                    setPdfUrl('');
                    setSendOptions({ email: false, whatsapp: false, print: false });
                }, title: "Or\u00E7amento - Visualizar e Enviar", size: "xl", children: _jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "bg-gray-50 rounded-lg p-4", children: [_jsxs("h3", { className: "font-medium text-gray-900 mb-2", children: ["Cliente: ", selectedOS?.cliente.nome] }), _jsxs("div", { className: "grid grid-cols-2 gap-4 text-sm text-gray-600", children: [_jsxs("div", { children: ["\uD83D\uDCE7 ", selectedOS?.cliente.email || 'Email não cadastrado'] }), _jsxs("div", { children: ["\uD83D\uDCF1 ", selectedOS?.cliente.telefonePrincipal || 'Telefone não cadastrado'] })] })] }), _jsxs("div", { className: "border rounded-lg overflow-hidden", children: [_jsxs("div", { className: "bg-gray-100 px-4 py-2 flex items-center justify-between", children: [_jsx("h4", { className: "font-medium text-gray-700", children: "Preview do Cupom PDF" }), _jsxs("div", { className: "text-sm text-gray-600", children: ["Valor Total: ", formatCurrency(calcularValorTotal())] })] }), _jsx("div", { className: "bg-gray-50", style: { height: '600px' }, children: pdfUrl ? (_jsx("iframe", { src: pdfUrl, className: "w-full h-full border-0", title: "Preview Cupom PDF Or\u00E7amento" })) : isGeneratingCupom ? (_jsx("div", { className: "h-full flex items-center justify-center", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" }), _jsx("p", { className: "mt-2 text-gray-600", children: "Gerando preview do cupom PDF..." })] }) })) : (_jsx("div", { className: "h-full flex items-center justify-center", children: _jsxs("div", { className: "text-center text-gray-500", children: [_jsx(FileText, { className: "h-12 w-12 mx-auto mb-2 opacity-50" }), _jsx("p", { children: "Preview n\u00E3o dispon\u00EDvel" })] }) })) })] }), _jsxs("div", { className: "space-y-3", children: [_jsx("h4", { className: "font-medium text-gray-900", children: "Como deseja proceder com o or\u00E7amento?" }), _jsxs("label", { className: "flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: sendOptions.email, onChange: (e) => setSendOptions({ ...sendOptions, email: e.target.checked }), className: "rounded border-gray-300", disabled: !selectedOS?.cliente.email }), _jsx(Mail, { className: "h-5 w-5 text-blue-500" }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: "font-medium", children: "Enviar por Email" }), _jsx("div", { className: "text-sm text-gray-500", children: selectedOS?.cliente.email || 'Email não cadastrado' })] })] }), _jsxs("label", { className: "flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: sendOptions.whatsapp, onChange: (e) => setSendOptions({ ...sendOptions, whatsapp: e.target.checked }), className: "rounded border-gray-300", disabled: !selectedOS?.cliente.telefonePrincipal }), _jsx(MessageCircle, { className: "h-5 w-5 text-green-500" }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: "font-medium", children: "Enviar por WhatsApp" }), _jsx("div", { className: "text-sm text-gray-500", children: selectedOS?.cliente.telefonePrincipal || 'Telefone não cadastrado' })] })] }), _jsxs("label", { className: "flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: sendOptions.print, onChange: (e) => setSendOptions({ ...sendOptions, print: e.target.checked }), className: "rounded border-gray-300" }), _jsx(Printer, { className: "h-5 w-5 text-gray-700" }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: "font-medium", children: "Imprimir" }), _jsx("div", { className: "text-sm text-gray-500", children: "Abre o PDF em nova aba para impress\u00E3o" })] })] })] }), _jsx("div", { className: "bg-yellow-50 border border-yellow-200 rounded-lg p-4", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Clock, { className: "h-5 w-5 text-yellow-600 mt-0.5" }), _jsxs("div", { className: "text-sm text-yellow-800", children: [_jsx("p", { className: "font-medium", children: "Importante:" }), _jsxs("p", { children: ["Qualquer uma das op\u00E7\u00F5es acima (Email, WhatsApp ou Impress\u00E3o) ir\u00E1 automaticamente alterar o estado da ordem de servi\u00E7o para ", _jsx("strong", { children: "\"Aguarda Aprova\u00E7\u00E3o\"" }), "."] })] })] }) }), _jsxs("div", { className: "flex justify-between gap-3 pt-4 border-t", children: [_jsx(Button, { variant: "outline", onClick: fecharModalSemEnviar, children: "N\u00E3o Enviar Agora" }), _jsx(Button, { onClick: confirmarEnvio, leftIcon: _jsx(Send, { className: "h-4 w-4" }), disabled: !sendOptions.email && !sendOptions.whatsapp && !sendOptions.print, children: "Executar A\u00E7\u00F5es Selecionadas" })] })] }) })] }));
};
export default OrcamentoFormPage;
